import { BeatLikeOffer, CardHeaderProps, InsightButtonProps } from '../components'

export interface Account {
  account_number?: string
  account_subtype?: AccountSubType
  account_type: AccountType
  apr?: number
  apy?: number
  available_balance?: number
  balance?: number
  balance_updated_at?: number
  cash_balance?: number
  connectionStatus?: ConnectionStatus
  credit_limit?: number
  currency_code?: string
  current_balance?: number
  day_payment_is_due?: number
  displayName?: string
  display_order?: number
  external_guid?: string
  feed_account_type?: number
  feed_apr?: number
  feed_apy?: number
  feed_credit_limit?: number
  feed_day_payment_is_due?: number
  feed_interest_rate?: number
  feed_is_closed?: boolean
  feed_localized_name?: string
  feed_name?: string
  feed_nickname?: string
  feed_original_balance?: number
  flags?: number
  guid: string
  institutionName?: string
  institution_guid?: string
  interestRate?: number
  interest_rate?: number
  interest_rate_set_by?: number
  is_closed: boolean
  is_deleted: boolean
  is_excluded_from_accounts: boolean
  is_excluded_from_budgets: boolean
  is_excluded_from_cash_flow: boolean
  is_excluded_from_debts: boolean
  is_excluded_from_goals: boolean
  is_excluded_from_investments: boolean
  is_excluded_from_net_worth: boolean
  is_excluded_from_spending: boolean
  is_excluded_from_transactions: boolean
  is_excluded_from_trends: boolean
  is_hidden: boolean
  is_manual: boolean
  is_personal: boolean
  localized_name?: string
  memberName?: string
  member_guid?: string
  member_is_managed_by_user: boolean
  metadata?: string
  minimum_payment?: number
  name?: string
  nickname?: string
  original_balance?: number
  payment_due_at?: number
  pending_balance?: number
  property_type?: PropertyType
  revision?: number
  truncatedDisplayName?: string
  updated_at?: number
  user_guid?: string
  user_name?: string
}

export interface Beat {
  account_guids: string[] | []
  active_at?: string | number
  associated_transaction_guid?: string | null
  call_to_action?: string | null
  category_guids?: string[] | []
  created_at?: string | number
  cta_clicked_at?: number | null
  data_series: DataSeries
  description?: string
  digest?: string
  dismissed_at?: number | null
  displayed_at?: string | null
  errors?: any
  guid: string
  has_been_displayed: boolean
  html_description?: string
  html_in_description_cta?: string
  html_micro_call_to_action?: string
  html_micro_description?: string
  html_micro_title?: string
  html_short_title?: string
  html_title: string
  icon_color?: string | null
  icon_type?: number | null
  in_description_cta?: string | null
  is_dismissed: boolean
  is_obsolete: boolean
  is_refreshable: boolean
  is_sampled: boolean
  merchant_guids: string[] | []
  metadata?: string | null
  micro_call_to_action?: string | null
  micro_description?: string | null
  micro_title?: string | null
  payload: any
  primary_account?: Account
  primary_account_guid?: string | null
  primary_category_guid?: string | null
  primary_goal_guid?: string | null
  primary_merchant_guid?: string | null
  primary_scheduled_payment_guid?: string | null
  primary_transaction?: Transaction
  primary_transaction_guid?: string | null
  power_action_completed_at?: string | null
  power_action_has_been_completed?: boolean
  scheduled_payment_guids: string[] | []
  short_title?: string | null
  supporting_action?: string | null
  template: string
  title: string
  transaction_guids: string[] | []
  transactions?: Transaction[]
  user_guid: string
  user_verification: UserVerification
}

interface ButtonProps extends Omit<InsightButtonProps, 'beat' | 'onClick'> {
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, type?: ButtonActionType) => void
}

export interface CommonBeatProps {
  altLoadingText?: string
  asteriskText?: string
  beat: Beat | BeatLikeOffer
  buttonProps?: ButtonProps
  errorText?: string
  feedback?: React.ReactNode | null
  header?: Omit<CardHeaderProps, 'title'>
  institutionName?: string
  isError?: boolean
  isLoading?: boolean
  isMini?: boolean
  isZeroState?: boolean
}

export interface DataSeries {
  data?: any[]
  names?: string[]
  types?: string[]
}

export interface Transaction {
  account?: string
  accountIsClosed?: boolean
  accountIsHidden?: boolean
  account_guid: string
  amount: number
  attachment_guid?: string | null
  category?: string
  category_guid?: string
  check_number?: string | null
  currency_code: string | null
  date: number
  description: string
  duplicate: boolean
  external_guid?: string | null
  feed_attachment_guid?: string | null
  feed_description: string
  feed_localized_description?: string | null
  feed_posted_date?: number | null
  feed_status: TransactionStatus | null
  feed_transaction_date: number | null
  feed_transacted_at: number | null
  flags?: number | null
  guid: string
  has_been_split: boolean
  has_been_viewed: boolean
  isIncome?: boolean
  isPending?: boolean
  is_cleared: boolean
  is_duplicate: boolean
  is_flagged: boolean
  is_hidden: boolean
  is_international?: boolean | null
  is_manual: boolean
  is_personal: boolean
  is_reimbursable: boolean
  is_subscription: boolean
  is_void: boolean
  localized_description?: string | null
  localized_memo?: string | null
  memo?: string | null
  merchant_guid?: string | null
  metadata?: string | null
  number?: string
  parent_guid?: string | null
  payee?: string
  posted_date?: number | null
  reference?: string | null
  revision: number
  scheduled_payment_guid?: string | null
  tags: string[]
  top_level_category_guid?: string
  transaction_type: TransactionType
  user_date?: number | null
  user_guid: string
  user_transaction_rule_guid?: string | null
}

export enum AccountSubType {
  ACCIDENTAL_DEATH_AND_DISMEMBERMENT = 40,
  ADVISORY_ACCOUNT = 61,
  AUTO = 3,
  BOAT = 25,
  BROKERAGE = 17,
  BROKERAGE_MARGIN = 62,
  CASH_MANAGEMENT_ACCOUNT = 46,
  CERTIFICATE_OF_DEPOSIT = 2,
  CHARITABLE_GIFT_ACCOUNT = 63,
  CHURCH_ACCOUNT = 64,
  CONSERVATORSHIP = 65,
  CORPORATE_ACCOUNT = 104,
  COVERDELL = 60,
  CUSTODIAL = 66,
  DEFINED_BENEFIT_PLAN = 67,
  DEFINED_CONTRIBUTION_PLAN = 68,
  DISABILITY = 33,
  EDUCATIONAL = 69,
  EMPLOYEE_STOCK_OWNERSHIP_PLAN = 22,
  EMPLOYEE_STOCK_PURCHASE_PLAN = 47,
  ESTATE = 70,
  EXECUTOR = 71,
  FIXED_ANNUITY = 30,
  FIXED_ANNUITY_ROTH_IRA = 57,
  FIXED_ANNUITY_TRADITIONAL_IRA = 53,
  GROUP_RETIREMENT_SAVINGS_PLAN = 72,
  GUARANTEED_INVESTMENT_CERTIFICATE = 73,
  HEALTH = 34,
  HELOC = 28,
  HOME_EQUITY = 8,
  HRA = 74,
  HSA = 42,
  HSA_CHECKING = 107,
  HSA_SAVINGS = 106,
  INDEXED_ANNUITY = 75,
  INDIVIDUAL = 44,
  INHERITED_ANNUITY = 103,
  INHERITED_ROTH_IRA = 59,
  INHERITED_TRADITIONAL_IRA = 56,
  INVESTMENT_CLUB = 76,
  IRA = 12,
  IRREVOCABLE_TRUST = 77,
  JOINT_TENANTS_BY_ENTIRITY = 78,
  JOINT_TENANTS_COMMUNITY_PROPERTY = 79,
  JOINT_TENANTS_IN_COMMON = 80,
  JOINT_TENANTS_WITH_RIGHTS_OF_SURVIVORSHIP = 81,
  KEOUGH_PLAN = 82,
  LIFE_INCOME_FUND = 83,
  LIMITED_LIABILITY_ACCOUNT = 105,
  LIVING_TRUST = 84,
  LOCKED_IN_RETIREMENT_ACCOUNT = 85,
  LOCKED_IN_RETIREMENT_INVESTMENT_FUND = 86,
  LOCKED_IN_RETIREMENT_SAVINGS_ACCOUNT = 87,
  LONG_TERM_CARE = 35,
  MONEY_MARKET = 1,
  MONEY_PURCHASE_PLAN = 88,
  NON_TAXABLE = 16,
  NONE = 0,
  PARTNERSHIP = 89,
  PENSION = 21,
  PERSONAL = 6,
  PERSONAL_WITH_COLLATERAL = 7,
  PLAN_401_A = 51,
  PLAN_401_K = 9,
  PLAN_403_B = 10,
  PLAN_409_A = 90,
  PLAN_457 = 20,
  PLAN_529 = 11,
  PLAN_ROTH_401_K = 29,
  PLAN_ROTH_403_B = 91,
  POWERSPORTS = 26,
  PROFIT_SHARING_PLAN = 49,
  PROPERTY_AND_CASUALTY = 36,
  REGISTERED_DISABILITY_SAVINGS_PLAN = 92,
  REGISTERED_EDUCATION_SAVINGS_PLAN = 48,
  REGISTERED_LOCKED_IN_SAVINGS_PLAN = 93,
  REGISTERED_PENSION_PLAN = 94,
  REGISTERED_RETIREMENT_INCOME_FUND = 45,
  REGISTERED_RETIREMENT_SAVINGS_PLAN = 95,
  REVOCABLE_TRUST = 96,
  ROLLOVER_IRA = 13,
  ROTH_CONVERSION = 97,
  ROTH_IRA = 14,
  RV = 27,
  SARSEP_IRA = 52,
  SEPP_IRA = 55,
  SIMPLE_IRA = 24,
  SIMPLIFIED_EMPLOYEE_PENSION = 23,
  SMALL_BUSINESS = 5,
  SOLE_PROPRIETORSHIP = 98,
  SPOUSAL_IRA = 99,
  SPOUSAL_ROTH_IRA = 100,
  STUDENT = 4,
  TAXABLE = 15,
  TAX_FREE_SAVINGS_ACCOUNT = 43,
  TERM_LIFE = 38,
  TESTAMENTARY_TRUST = 101,
  THRIFT_SAVINGS_PLAN = 102,
  TRUST = 18,
  UNIFORM_GIFTS_TO_MINORS_ACT = 19,
  UNIFORM_TRANSFER_TO_MINORS_ACT = 50,
  UNIVERSAL_LIFE = 37,
  VARIABLE_ANNUITY = 31,
  VARIABLE_ANNUITY_ROTH_IRA = 58,
  VARIABLE_ANNUITY_TRADITIONAL_IRA = 54,
  VARIABLE_UNIVERSAL_LIFE = 41,
  VEHICLE_INSURANCE = 32,
  WHOLE_LIFE = 39,
}

export enum AccountType {
  ANY = 0,
  CASH = 9,
  CHECKING = 1,
  CHECKING_LINE_OF_CREDIT = 12,
  CREDIT_CARD = 4,
  DIGITAL_WALLET = 13,
  INSURANCE = 10,
  INVESTMENT = 5,
  LINE_OF_CREDIT = 6,
  LOAN = 3,
  MORTGAGE = 7,
  PREPAID = 11,
  PROPERTY = 8,
  SAVINGS = 2,
}

export enum ConnectionStatus {
  Connected = 'Connected',
  Error = 'Error',
  Processing = 'Processing',
  RequiresMfa = 'RequiresMfa',
}

export enum PropertyType {
  APPLIANCES = 5,
  ART = 2,
  COMPUTER = 6,
  ELECTRONICS = 7,
  FURNITURE = 4,
  JEWELRY = 3,
  MISCELLANEOUS = 9,
  REAL_ESTATE = 0,
  SPORTS_EQUIPMENT = 8,
  VEHICLE = 1,
}

export enum TransactionStatus {
  PENDING = 2,
  POSTED = 1,
}

export enum TransactionType {
  CREDIT = 1,
  DEBIT = 2,
}

export enum UserVerification {
  CORRECT = 1,
  INCORRECT = 2,
  UNKNOWN = 0,
}

export enum ButtonActionType {
  Primary = 'Primary',
  Secondary = 'Secondary',
}
